<template>
  <div>
    <vx-card class="mb-10" :noShadow="true">
      <div class="flex">
        <h5 class="font-medium text-2xl">{{ $t('Campaign Details') }}</h5>
        <speakol-tooltip class="campaign-details-tooltip"
          :tooltipRedirect="'https://speakol.zendesk.com/hc/en-us/articles/360016737959-Campaign-Details'"
          :tooltipAction="'Learn more here'"
          :tooltipText="'Every campaign has a unique set of details that differentiate between it and other campaigns, including the name, assigned subaccount, among other properties.'">
        </speakol-tooltip>
      </div>

      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full mt-8">
          <label class="e-campaign-name">{{ $t('Campaign Name') }}</label>

          <vs-input id="create-campaign-s2-campaign-name-input" v-model="value.name" name="step2.name" v-validate="'required|max:100'"
            :data-vv-as="$t('Campaign Name')" data-vv-validate-on="blur"
            :danger="$vErrors.has([errors, serverErrors], 'step2.name')" :placeholder="$t('Campaign Name')"
            class="w-full e-campaign-name-input" maxLength="100" />

          <span v-if="$vErrors.has([errors, serverErrors], 'step2.name')" class="text-danger">
            {{ $vErrors.first([errors, serverErrors], 'step2.name') }}
          </span>
        </div>

        <div class="vx-col md:w-1/2 w-full mt-8">
          <div class="brand-name-parent">
            <label>{{ $t('Brand Name') }}</label>
            <vx-tooltip text="It will be displayed next to your ad on the Speakol widget." position="right">
              <vs-icon class="flex justify-center items-center tooltip-icon" icon-pack="speakol" icon="speakol-info" :color="'#2C2C2C'"></vs-icon>
            </vx-tooltip>
            <vs-input id="create-campaign-s2-brand-name-input" v-model="value.brand" name="step2.brand" v-validate="'required|max:25'"
              :data-vv-as="$t('Brand Name')" data-vv-validate-on="blur"
              :danger="$vErrors.has([errors, serverErrors], 'step2.brand')" :placeholder="$t('Brand Name')"
              class="w-full e-brand-name-input" maxLength="25" />
          </div>

          <span v-if="$vErrors.has([errors, serverErrors], 'step2.brand')" class="text-danger">
            {{ $vErrors.first([errors, serverErrors], 'step2.brand') }}
          </span>
        </div>

        <div class="vx-col md:w-1/2 w-full mt-8">
          <label class="input-label">{{ $t('Industry') }}</label>

          <v-select @input="$gtag.event('Industry')" id="create-campaign-s2-industry-list" name="step2.industry_id"
            :disabled="$can('review_campaigns') ? false : (editing && !$route.query.duplicate)"
            :data-vv-as="$t('Industry')" v-validate="'required'" v-model="value.industry_id" label="name"
            :reduce="o => o.id" :options="industryOptions" class="baisc-select mt-3 mb-3"
            :placeholder="$t('Not Selected')" :dir="$vs.rtl ? 'rtl' : 'ltr'" />

          <span v-if="$vErrors.has([errors, serverErrors], 'step2.industry_id')" class="text-danger">
            {{ $vErrors.first([errors, serverErrors], 'step2.industry_id') }}
          </span>
        </div>

        <div class="vx-col md:w-1/2 w-full mt-8" v-if="!isLeadFormCampaign">
          <div class="brand-name-parent">
            <label class="input-label">{{ $t('create_campaign.action_label') }}
              <span v-if="!isCampaignOfObjectiveX(conversionsObjective) && !isCampaignOfObjectiveX(facebookConversionLeadObjective)">
                {{ optional }}
              </span>
            </label>
            <vx-tooltip :text="$t('create_campaign.action_tooltip')" position="right">
              <vs-icon class="flex justify-center items-center tooltip-icon ml-1" icon-pack="speakol" icon="speakol-info" :color="'#2C2C2C'"></vs-icon>
            </vx-tooltip>

            <vs-input
              v-if="isActionInputDisabled || (isCampaignOfObjectiveX(conversionsObjective) || isCampaignOfObjectiveX(facebookConversionLeadObjective))"
              id="create-campaign-s2-main-event-input"
              :value="eventName | formatMainEvent"
              class="w-full" disabled
            />

            <v-select v-else
              id="create-campaign-s2-events-list"
              name="step2.main_event"
              :disabled="isActionInputDisabled"
              label="title"
              :data-vv-as="$t('Action')"
              :options="pixelEvents"
              class="baisc-select mt-3 mb-3"
              v-model="value.main_event"
              :reduce="o => o.key"
              :placeholder="$t('Not Selected')"
            />

            <span v-if="$vErrors.has([errors, serverErrors], 'step2.main_event')" class="text-danger">
              {{ $vErrors.first([errors, serverErrors], 'step2.main_event') }}
            </span>
          </div>
        </div>
      </div>
    </vx-card>

    <vx-card :noShadow="true" class="section-margin-mobile-version sm:mb-5">
      <h5 class="font-medium text-2xl mt-6 pb-4">{{ $t('Start & End Date') }}</h5>
      <!-- <h5 class="text-base font-normal text-gray-500 mt-4 mb-8">{{ $t('Please fill in your campaign start and end dates.') }}</h5> -->

      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full mt-5">
          <h5 class="mb-10 e-start-date-label">
            <span class="inline-block align-middle mr-3">
              <vs-icon icon-pack="speakol" icon="speakol-calendar" />
            </span>
            <span class="inline-block align-middle font-normal">
              {{ $t('Start Date') }}
            </span>
          </h5>
          <div class="vs-col w-full mb-4 ml-1">
            <vs-radio @input="$gtag.event('Step2_Onceapproved')" id="create-campaign-s2-start-date-once-approved-checkbox" v-model="value.once_it_approved" vs-name="once_it_approved" vs-value="true"
              :disabled="editing && !$route.query.duplicate">
              {{ $t("Once it's approved") }}
            </vs-radio>
            <br>
            <br>
            <vs-radio @input="$gtag.event('Step2_Startdate')" id="create-campaign-s2-select-start-date-checkbox" v-model="value.once_it_approved" vs-name="once_it_approved" vs-value="false"
              :disabled="editing && !$route.query.duplicate">
              {{ $t("Select start date") }}
            </vs-radio>
          </div>
          <div class="vs-col w-full" v-if="value.once_it_approved != 'true'">
            <flat-pickr @input="$gtag.event('Step2_StartdateCal')" id="create-campaign-s2-start-date-date-picker-input" class="e-start-date-input mb-3" v-model="value.start_date" name="step2.start_date"
              :disabled="(editing || value.once_it_approved == 'true') && !$route.query.duplicate && !$route.query.start_now" />

            <span v-if="$vErrors.has([errors, serverErrors], 'step2.start_date')" class="text-danger">
              {{ $vErrors.first([errors, serverErrors], 'step2.start_date') }}
            </span>
          </div>

          <div v-if="(new Date).toISOString().split('T')[0] == value.start_date && value.once_it_approved != 'true'"
            class="start-date-hint-parent">
            <vs-icon icon-pack="speakol" class="start-date-hint-icon" icon="speakol-alert-circle" />
            <span>
              {{ $t("The approval cycle will take up to 48 hours and your campaign will start once it's approved.") }}
            </span>
          </div>
        </div>

        <div class="vx-col md:w-1/2 w-full mt-5">
          <h5 class="mb-10 e-end-date-label">
            <span class="inline-block align-middle font-normal">
              {{ $t('End Date') }}
            </span>
          </h5>

          <div class="vs-col w-full mb-4">
            <vs-radio @input="$gtag.event('Step2_Noend')" id="create-campaign-s2-no-end-date-checkbox" v-model="value.no_end_date" vs-name="no_end_date" vs-value="true">
              {{ $t("No end date") }}
            </vs-radio>
            <br>
            <br>
            <vs-radio @input="$gtag.event('Step2_Enddate')" id="create-campaign-s2-select-end-date-checkbox" v-model="value.no_end_date" vs-name="no_end_date" vs-value="false">
              {{ $t("Select end date") }}
            </vs-radio>
          </div>

          <div class="vs-col w-full" v-if="value.no_end_date != 'true'">
            <flat-pickr @input="$gtag.event('Step2_EnddateCal')" id="create-campaign-s2-end-date-date-picker-input" class="mb-3 e-end-date-input" v-validate="'required'" name="step2.end_date"
              :data-vv-as="$t('End Date')" v-model="value.end_date" :placeholder="$t('End Date')"
              :disabled="value.no_end_date == 'true'" />

            <span v-if="$vErrors.has([errors, serverErrors], 'step2.end_date')" class="text-danger">
              {{ $vErrors.first([errors, serverErrors], 'step2.end_date') }}
            </span>
          </div>
        </div>
      </div>
    </vx-card>

    <NewBudgetWhenEndDateChanged
      :adsetBudgets="adsetBudgets"
      v-if="editing && ($route.query.duplicate || 0) == 0 && editingBudgetType" :editingBudgetType="editingBudgetType"
      :objective="objective"
      :startDate="value.start_date"
      :endDate="value.end_date"
      :noEndDate="value.no_end_date"
      @confirm-new-budget-and-update="confirmNewBudgetAndUpdate"
      @cancel-new-budget="cancelNewBudget"
    />
  </div>
</template>


<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/themes/airbnb.css'
import NewBudgetWhenEndDateChanged from '@/views/campaigns/components/steps/components/BudgetContent/NewBudgetWhenEndDateChanged.vue'
import SpeakolTooltip from '@/components/speakol-template/SpeakolTooltip.vue'

import {get} from 'lodash'

export default {
  filters: {
    formatMainEvent (value) {
      return value ? (value.charAt(0).toUpperCase() + value.slice(1)).replace(/_/g, ' ') : null
    }
  },

  props: {
    value: {
      required: true,
      type: Object
    },
    serverErrors: {
      required: true
    },
    adsetBudgets: {
      type: Array,
      required: false
    },
    editing: {
      required: false,
      type: Boolean,
      default: false
    },
    objective: {
      required: false,
      type: Object
    }
  },
  components: {
    flatPickr,
    NewBudgetWhenEndDateChanged,
    SpeakolTooltip
  },
  watch: {
    'value.end_date' (newEndDate) {
      const dateFormat = 'YYYY-MM-DD'

      if (newEndDate) {
        const oldEndDate = this.$moment(this.campEndDate, dateFormat)
        newEndDate = this.$moment(newEndDate, dateFormat)

        if (newEndDate.isBefore(this.$moment(new Date()).format(dateFormat))) {
          this.editingBudgetType = false
        } else if (
          !this.campEndDate ||
          (this.campEndDate && newEndDate.isAfter(oldEndDate))
        ) {
          this.editingBudgetType = true
        }
      }
    },
    'value.no_end_date' (newVal) {
      if (newVal === 'true') {
        this.editingBudgetType = true
        this.value.end_date = null
      }
    },
    'value.once_it_approved' () {
      if (this.value.once_it_approved === 'true') {
        this.value.start_date = null
      } else {
        this.value.start_date = (new Date).toISOString().split('T')[0]
        this.value.end_date = null
      }

      // clear start date errors
      this.$vErrors.remove([this.errors, this.serverErrors], 'step2.start_date')
    },
    value: {
      async handler (newVal) {
        if (this.isCampaignOfObjectiveX(this.conversionsObjective) || this.isCampaignOfObjectiveX(this.facebookConversionLeadObjective)) {
          newVal.main_event = 'lead'
        }

        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  data () {
    return {
      editingBudgetType: false,
      isEventSet: !!this.value.main_event,
      campEndDate: this.value.end_date,
      conversionsObjective: this.$constants.CAMPAIGN_OBJECTIVES_IDS.CONVERSION,
      facebookConversionLeadObjective: this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_LEADS,
      facebookConversionSalesObjective: this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_SALES,
      isLeadFormCampaign: this.isCampaignOfObjectiveX(this.$constants.CAMPAIGN_OBJECTIVES_IDS.LEAD_FORM),
      optional: !this.isCampaignOfObjectiveX(this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_SALES) ? `(${this.$t('create_campaign.optional')})` : ''
    }
  },

  computed: {
    targetingLookups () {
      return this.$store.getters['campaign/targetingLookups']
    },

    industryOptions () {
      return this.targetingLookups.industries
    },

    pixelEvents () {
      const events = Object.values(get(this.targetingLookups, 'pixel_tracking_events.events', {}))

      if (this.isCampaignOfObjectiveX(this.facebookConversionSalesObjective)) {
        return events.filter(event => event.key === 'purchase' || event.key === 'add_to_cart' || event.key === 'add_to_wishlist')
      }

      return events.filter(event => event.key !== 'page_views')
    },

    isActionInputDisabled () {
      return (
        (this.editing && this.isEventSet && !this.$route.params.duplicate) ||
        (this.isCampaignOfObjectiveX(this.conversionsObjective) ||
          this.isCampaignOfObjectiveX(this.facebookConversionLeadObjective))
      )
    },

    eventName () {
      if (this.isCampaignOfObjectiveX(this.conversionsObjective) || this.isCampaignOfObjectiveX(this.facebookConversionLeadObjective)) {
        return 'Lead'
      }

      if (this.isCampaignOfObjectiveX(this.facebookConversionSalesObjective)) {
        return 'Purchase'
      }

      return this.value.main_event
    }
  },

  methods: {
    isCampaignOfObjectiveX (objective) {
      return this.objective.objective === objective ||
        this.objective.display_name === objective ||
        this.objective.objective_id === objective
    },

    hideModal () {
      setTimeout(() => {
        this.editingBudgetType = false
      }, 0)
    },

    confirmNewBudgetAndUpdate (newAdsetBudgets) {
      this.$emit('adsets-budgets-updated', newAdsetBudgets)
      if (this.value.no_end_date === 'true') {
        this.value.end_date = null
      }

      this.$emit('update-campaign-info', true)
      this.hideModal()
    },
    cancelNewBudget () {
      this.value.no_end_date = this.value.no_end_date === 'false' ? 'true' : 'false'

      this.hideModal()
    },

    handleStep2Update () {
      if (this.value.main_event) {
        this.isEventSet = true
      }
    }
  }
}

</script>

<style lang="scss">
  .tooltip-icon {
    font-size: 14px;
    margin-left: 9.5px;
  }

  .campaign-details-tooltip {
    position: relative;
    top: -2px;
  }

  .brand-name-parent {
    position: relative;

    img {
      width: 17px;
      margin-left: 5px;
      position: relative;
      top: 3px;
      cursor: pointer;
    }

    .con-vs-tooltip {
      display: inline-block;
    }
  }

  .vs__dropdown-toggle {
    height: 36px !important;
  }

  .start-date-hint-parent {
    color: #f2af03;
    margin-left: 25px;

    .start-date-hint-icon {
      width: 20px;
      position: absolute !important;
      left: 19px;
      margin-right: 12px;
    }
  }

  .section-margin-mobile-version {
    margin-bottom: 30px;
  }

</style>
